/**
 * Cognito Based Member Center SDK V1
 *
 * For usage see sdk/tester.html
 *
 * Old versions of Amplify JS in this repo do not tree shaking.
 * This leads to SDK  > 2.9MB. As a result, we use Cognito JS
 * libs directly.
 *
 */
const { subscribe, publish, unsubscribe } = require("tiny-pubsub");
import { CookieStorage, CognitoUserPool } from "amazon-cognito-identity-js";
import CONFIG from "../config";

const LOG_PREF = "[MCSDK]";
const POLL_INTERVAL = 4000;

// TODO: Issue these keys to consumers and make them pass into this SDK
const TENANT_CONFIG_APIKEY = CONFIG.apiGateway.APIKEY;

const TENANT_CONFIG_BASE_URL = CONFIG.apiGateway.URL.concat(
  "/configs/?station="
);

const USER_PROFILE_BASE_URL = CONFIG.apiGateway.URL.concat("/profiles");

const USER_NEWSLETTERS_BASE_URL = CONFIG.apiGateway.URL.concat(
  "/newsletters/subscriptions/?station="
);

const USER_NEWSLETTERS_POST_BASE_URL = CONFIG.apiGateway.URL.concat(
  "/newsletters/subscriptions"
);

const SIMPLE_SIGNUP_NEWSLETTERS_BASE_URL = CONFIG.apiGateway.URL.concat(
  "/newsletters/simple-signup"
);

const EMAIL_VERIFICATION_BASE_URL = CONFIG.apiGateway.URL.concat(
  "/email/simple-verification"
);

const SIMPLE_PROFILE_UPDATE_BASE_URL = CONFIG.apiGateway.URL.concat(
  "/simpleprofiles"
);

const TOKEN_PREFIX = `CognitoIdentityServiceProvider.${CONFIG.cognito.userPoolWebClientId}`;

const userPool = new CognitoUserPool({
  UserPoolId: CONFIG.cognito.userPoolId,
  ClientId: CONFIG.cognito.userPoolWebClientId,
  Storage: new CookieStorage(CONFIG.cognito.cookieStorage)
});
window.userPool = userPool;

class CMGMemberCenterSdk {
  // private - use: const mcsdk = await CMGMemberCenterSdk.create(config)
  constructor() {}

  static async create(config) {
    if (!config) {
      throw new Error("Must provide Tenant Config");
    }
    const _mcsdk = new CMGMemberCenterSdk(config);
    _mcsdk.config = config;
    await _mcsdk.initialize();
    return _mcsdk;
  }

  async initialize() {
    this.publish = publish;
    this.subscribe = subscribe;
    this.unsubscribe = unsubscribe;

    this.tenantConfigUrl = `${TENANT_CONFIG_BASE_URL}${this.config.tenant}`;
    this.tenantConfig = await this.fetchTenantConfig(this.tenantConfigUrl);
    /*
    // use local MC FE
    this.tenantConfig._extendedMeta = {
      ...this.tenantConfig._extendedMeta,
      "memberCenterUrl": "http://localhost:3000",
      "editProfileUrl": "http://localhost:3000/edit-profile",
      "signInUrl": "http://localhost:3000/sign-in",
      "signOutUrl": "http://localhost:3000/account/logout",
    }
    */

    this.user = null;
    this.userProfile = null;
    this.lastUserEventPublished = "";
    this.cognitoUser = null;
  }

  async startPolling() {
    await this.checkForUser();
    setInterval(() => {
      this.checkForUser();
    }, POLL_INTERVAL);
  }

  async fetchTenantConfig(tenantConfigUrl) {
    console.log(LOG_PREF, "fetching tenantConfig");
    return fetch(tenantConfigUrl, {
      headers: {
        "x-api-key": TENANT_CONFIG_APIKEY
      }
    })
      .then(response => {
        return response.json();
      })
      .then(data => {
        return Promise.resolve(data);
      })
      .catch(() => {
        return Promise.reject("Error fetching Tenant Config");
      });
  }

  getCognitoCookies() {
    const cookies = document.cookie.split(";");
    const cognitoCookies = {
      idToken: null,
      accessToken: null,
      refreshToken: null
    };
    cookies.forEach(cookie => {
      const [name, value] = cookie.trim().split("=");
      if (name.startsWith(TOKEN_PREFIX)) {
        if (name.endsWith("idToken")) {
          cognitoCookies.idToken = value;
        } else if (name.endsWith("refreshToken")) {
          cognitoCookies.refreshToken = value;
        } else if (name.endsWith("accessToken")) {
          cognitoCookies.accessToken = value;
        }
      }
    });
    return cognitoCookies;
  }

  async checkForUser() {
    const {
      idToken = null,
      accessToken = null,
      refreshToken = null
    } = this.getCognitoCookies();

    if (!idToken || !accessToken || !refreshToken) {
      this.user = null;
    } else {
      try {
        const currentUser = userPool.getCurrentUser();
        if (currentUser) {
          // Sets Session on User Object, refreshes if needed.
          // https://github.com/amazon-archives/amazon-cognito-identity-js/blob/master/src/CognitoUser.js#L1016
          currentUser.getSession(async (e, currentSession) => {
            if (e) {
              throw new Error("Could not create session");
            } else {
              const cognitoIdToken = currentSession.idToken;
              this.cognitoUser = currentUser;
              await this.fetchProfile().catch(() => {
                console.log(LOG_PREF, "Could not fetch Profile");
              });

              this.user = {
                username: cognitoIdToken.payload["cognito:username"],
                uuid: cognitoIdToken.payload.sub,
                email: cognitoIdToken.payload.email,
                family_name: cognitoIdToken.payload.family_name,
                given_name: cognitoIdToken.payload.given_name,
                display_name:
                  this.userProfile?.displayName ||
                  cognitoIdToken.payload.nickname ||
                  cognitoIdToken.payload.given_name ||
                  cognitoIdToken.payload.family_name ||
                  cognitoIdToken.payload.name ||
                  cognitoIdToken.payload.email ||
                  ""
              };
            }
          });
        } else {
          this.user = null;
        }
      } catch (e) {
        this.user = null;
        console.log(LOG_PREF, e.message);
      }
    }

    // Publish event once per user state change
    if (
      this.user &&
      this.lastUserEventPublished !== "authenticatedUserDetected"
    ) {
      this.publish("authenticatedUserDetected", { user: this.user });
      this.lastUserEventPublished = "authenticatedUserDetected";
    } else if (
      !this.user &&
      this.lastUserEventPublished !== "anonymousUserDetected"
    ) {
      this.publish("anonymousUserDetected");
      this.lastUserEventPublished = "anonymousUserDetected";
    }
  }

  navigateToLogin(redirectBack = true) {
    const startingUri = encodeURIComponent(window.location.href);
    const action = redirectBack
      ? "&action=login&postAction=redirect"
      : "&action=login";
    window.location.replace(
      `${this.tenantConfig._extendedMeta.signInUrl}?${action}&referrer=${startingUri}`
    );
  }

  openLoginWindow(closeAfterAction = true) {
    const startingUri = encodeURIComponent(window.location.href);
    const action = closeAfterAction
      ? "&action=login&postAction=close"
      : "&action=login";
    window.open(
      `${this.tenantConfig._extendedMeta.signInUrl}?${action}&referrer=${startingUri}`
    );
  }

  openLoginWindowToSetSession(closeAfterAction = true) {
    const startingUri = encodeURIComponent(window.location.href);
    const action = closeAfterAction
      ? "&action=login&postAction=close"
      : "&action=login";
    window.open(
      `${
        this.tenantConfig._extendedMeta.signInUrl
      }?${action}&referrer=${startingUri}&refreshToken=${
        this.cognitoUser.getSignInUserSession().refreshToken.token
      }`
    );
  }

  navigateToLogout(redirectBack = true) {
    const startingUri = encodeURIComponent(window.location.href);
    const action = redirectBack
      ? "&action=logout&postAction=redirect"
      : "&action=logout";
    this.signOut();
    window.location.replace(
      `${this.tenantConfig._extendedMeta.signOutUrl}?${action}&referrer=${startingUri}`
    );
  }

  openLogoutWindow() {
    this.openLogoutWindowToCloseSession();
  }

  openLogoutWindowToCloseSession(closeAfterAction = true) {
    const startingUri = encodeURIComponent(window.location.href);
    const action = closeAfterAction
      ? "&action=logout&postAction=close"
      : "&action=logout";
    this.signOut();
    window.open(
      `${this.tenantConfig._extendedMeta.signOutUrl}?${action}&referrer=${startingUri}`
    );
  }

  async fetchProfile() {
    if (this.cognitoUser) {
      const email = this.cognitoUser.signInUserSession.idToken.payload.email;
      return fetch(`${USER_PROFILE_BASE_URL}/${email}/`, {
        headers: {
          Authorization: this.cognitoUser.getSignInUserSession().idToken
            .jwtToken
        }
      })
        .then(response => {
          return response.json();
        })
        .then(profile => {
          this.userProfile = profile;
        })
        .catch(() => {
          return Promise.reject("Error fetching user Profile");
        });
    } else {
      return Promise.reject("No user signed in");
    }
  }

  fetchActiveNewsletterSubscriptions() {
    const userNewslettersUrl = USER_NEWSLETTERS_BASE_URL.concat(
      this.tenantConfig._extendedMeta.tenant
    );
    if (this.user) {
      return fetch(
        userNewslettersUrl + `&email=${encodeURIComponent(this.user.email)}`,
        {
          headers: {
            Authorization: this.cognitoUser.getSignInUserSession().idToken
              .jwtToken
          }
        }
      )
        .then(response => {
          return response.json();
        })
        .then(data => {
          return data.success
            ? data.success.filter(
                subscription => subscription.status === "active"
              )
            : [];
        })
        .catch(() => {
          return Promise.reject("Error fetching user newsletter subscriptions");
        });
    } else {
      return Promise.reject(
        "Error fetching user's active newsletters. There is no user"
      );
    }
  }

  getTenantNewsletters() {
    if (this.tenantConfig) {
      return this.tenantConfig.newsletters;
    } else {
      throw Error(
        "Error fetching Tenant Config newsletters. There is no tenantConfig"
      );
    }
  }

  subscribeToNewsletter(newsletterId, anonymousUserEmail, policyConsent) {
    let subscriptionResponse;

    if (this.user) {
      subscriptionResponse = fetch(
        USER_NEWSLETTERS_POST_BASE_URL +
          `/?email=${encodeURIComponent(this.user.email)}`,
        {
          method: "POST",
          mode: "cors",
          headers: {
            Authorization: this.cognitoUser.getSignInUserSession().idToken
              .jwtToken
          },
          body: JSON.stringify({
            newsletterId: newsletterId,
            status: "active",
            policyConsent
          })
        }
      );
    } else if (anonymousUserEmail) {
      subscriptionResponse = fetch(SIMPLE_SIGNUP_NEWSLETTERS_BASE_URL, {
        method: "POST",
        mode: "cors",
        headers: {
          "x-api-key": TENANT_CONFIG_APIKEY
        },
        body: JSON.stringify({
          email: anonymousUserEmail,
          newsletterId: newsletterId,
          status: "active",
          policyConsent
        })
      });
    } else {
      return Promise.reject(
        `Error subscribing to newsletter ${newsletterId}. No user email provided`
      );
    }

    return subscriptionResponse
      .then(response => {
        return response.json();
      })
      .catch(e => {
        if (e) {
          return Promise.reject(e);
        } else {
          return Promise.reject(
            `Error subscribing user to newsletter ${newsletterId}`
          );
        }
      });
  }

  createAnonymousProfile(email, zipCode) {
    return fetch(
      EMAIL_VERIFICATION_BASE_URL + `/?email=${encodeURIComponent(email)}`,
      {
        method: "POST",
        mode: "cors",
        headers: {
          "x-api-key": TENANT_CONFIG_APIKEY
        }
      }
    )
      .then(response => {
        return response.json();
      })
      .then(data => {
        if (data && data.isValid) {
          const zipCodeParam = zipCode ? `/?zipCode=${zipCode}` : "";

          return fetch(
            SIMPLE_PROFILE_UPDATE_BASE_URL + `/${email}` + zipCodeParam,
            {
              method: "PUT",
              mode: "cors",
              headers: {
                "x-api-key": TENANT_CONFIG_APIKEY
              }
            }
          );
        } else if (data && !data.isValid) {
          data.message && console.log(LOG_PREF, data.message);
          return Promise.reject("Please enter a valid email address");
        } else {
          console.log(
            LOG_PREF,
            "Email verification request returned no response"
          );
          return Promise.reject();
        }
      })
      .then(() => email);
  }

  deleteCognitoCookies() {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].split("=");
      const name = cookie[0].trim();
      if (name.startsWith(TOKEN_PREFIX)) {
        //console.log(LOG_PREF, "Delete cookie", name);
        document.cookie = `${name}=;Path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT;SameSite=None;Secure`;
      }
    }
  }

  signOut() {
    this.cognitoUser?.signOut();
    this.cognitoUser = null;
    this.user = null;
    this.userProfile = null;
    this.deleteCognitoCookies();
  }

  isAuthenticated() {
    return !!this.user;
  }
}

window.CMGMemberCenterSdk = window.CMGMemberCenterSdk || CMGMemberCenterSdk;
